/**
 * CurrencySelector — Compact navbar dropdown for switching display currency.
 *
 * Positioned between the search bar and notification bell.
 * Shows currency code (e.g. "USD") with a small dropdown arrow.
 */

import React, { useState } from 'react';
import {
  Typography,
  Menu,
  MenuItem,
  ButtonBase,
  ListItemText,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { typography } from '../common/styles/universalTheme';
import { useCurrency } from '../contexts/CurrencyContext';
import { CURRENCY_LABELS, DISPLAY_CURRENCIES } from '../utils/formatters';
import type { SupportedCurrency } from '../utils/formatters';

/** Currencies offered in the display-currency dropdown.
 *
 *  2026-06-15: temporarily restricted to USD / AUD / GBP / EUR / JPY
 *  while the FX conversion path is shaken out on the new dynamic browse
 *  pages (Sector + Exchange landings). The full SUPPORTED_CURRENCIES set
 *  is still defined in utils/formatters.ts — expanding back to all of
 *  them is a one-line revert here once conversion is verified end-to-end.
 *
 *  Display-only — risk analysis itself always runs in USD (see the
 *  Bradford dashboard's modeled-in-USD disclaimer). */

const CurrencySelector: React.FC = () => {
  const { displayCurrency, setDisplayCurrency } = useCurrency();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (currency: SupportedCurrency) => {
    setDisplayCurrency(currency);
    handleClose();
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-label="Select display currency"
        aria-haspopup="true"
        aria-expanded={open}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          sx={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            color: 'text.primary',
            letterSpacing: 0.5,
          }}
        >
          {displayCurrency}
        </Typography>
        <ExpandMore sx={{ fontSize: 14, color: 'text.secondary' }} />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 200,
              maxHeight: 320,
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
      >
        {DISPLAY_CURRENCIES.map((currency) => (
          <MenuItem
            key={currency}
            selected={currency === displayCurrency}
            onClick={() => handleSelect(currency)}
            sx={{
              py: 0.75,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
              },
            }}
          >
            <ListItemText
              primary={currency}
              secondary={CURRENCY_LABELS[currency].replace(` (${currency})`, '')}
              primaryTypographyProps={{
                fontSize: typography.fontSize.sm,
                fontWeight: currency === displayCurrency
                  ? typography.fontWeight.semibold
                  : typography.fontWeight.regular,
                color: 'text.primary',
              }}
              secondaryTypographyProps={{
                fontSize: typography.fontSize.xs,
                color: 'text.secondary',
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CurrencySelector;
